import { FC, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import LoadingSpinner from './LoadingSpinner';

interface AgentRouteProps {
  children: React.ReactNode;
}

/**
 * Guard rotas do Hub de Agentes.
 *
 * Fase 1 (POC) era ADMIN-only. Fase 2 (P04+P09) abriu pra qualquer user. Em
 * 2026-06 voltou a ser **gated por padrão** — LEARNER só passa se admin liberar
 * via `User.agent_feature_enabled` OU `Sector.agent_feature_enabled`. ADMIN e
 * CURATOR continuam sempre passando. Esta checagem é redundante com o backend
 * (que retorna 403 estruturado) — aqui é UX pra não deixar o user bater na 403.
 */
const AgentRoute: FC<AgentRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuthContext();
  const { showToast } = useToast();

  const blocked =
    !isLoading && isAuthenticated && user && !user.agent_feature_enabled;

  useEffect(() => {
    if (blocked) {
      showToast(
        'Hub de Agentes ainda não foi liberado pra você. Fale com seu admin.',
        'warning',
      );
    }
  }, [blocked, showToast]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (blocked) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AgentRoute;
